import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Select, 
  Spin, 
  message, 
  Breadcrumb, 
  Space,
  Typography
} from 'antd';
import { LoadingOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import SimpleEditor from '../ESR/editor/texteditor/texteditor';
import { 
  getProjectTemplate, 
  updateProjectTemplate, 
  getAreaCoveredDropdown 
} from '../../api/esr';
import { getProjectTypes } from '../../api/project_type';
import { 
  ESRProjectForm, 
  ESRTemplateState, 
  AreaCoveredOption, 
  AreaCoveredDropdownResponse 
} from '../../types/esr';
import './styles.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option, OptGroup } = Select;

interface ESRTemplateEditorProps {}

const ESRTemplateEditor: React.FC<ESRTemplateEditorProps> = () => {
  const { projectTypeId } = useParams<{ projectTypeId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [state, setState] = useState<ESRTemplateState>({
    loading: true,
    saving: false,
    formData: {
      title: '',
      content: '',
      content_after_icons: '',
      area_covered_ids: []
    },
    areaCoveredOptions: [],
    errors: {}
  });

  const [areaCoveredCategories, setAreaCoveredCategories] = useState<Record<string, AreaCoveredOption[]>>({});
  const [projectTypeName, setProjectTypeName] = useState<string>('');

  const [contentValue, setContentValue] = useState<any>(null);
  const [contentAfterIconsValue, setContentAfterIconsValue] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectTypeId) return;

      try {
        setState(prev => ({ ...prev, loading: true }));

        const [areaResponse, templateResponse, projectTypesResponse] = await Promise.all([
          getAreaCoveredDropdown(),
          getProjectTemplate(projectTypeId).catch(() => null),
          getProjectTypes()
        ]);

        const areaData = areaResponse.data || areaResponse.response || areaResponse;
        const templateData = templateResponse?.data || templateResponse?.response || templateResponse;
        const projectTypesData = projectTypesResponse.response || projectTypesResponse;

        const currentProjectType = projectTypesData.find((pt: any) => pt.id === projectTypeId);
        if (currentProjectType) {
          setProjectTypeName(currentProjectType.display_name);
        }

        setState(prev => ({ 
          ...prev, 
          areaCoveredOptions: areaData.flat || [],
        }));
        
        const categorizedObj: Record<string, AreaCoveredOption[]> = {};
        if (areaData.categorized && Array.isArray(areaData.categorized)) {
          areaData.categorized.forEach((category: any) => {
            categorizedObj[category.category_display_name] = category.areas;
          });
        }
        setAreaCoveredCategories(categorizedObj);

        let formData: ESRProjectForm;
        if (templateData) {
          const areaCoveredIds = templateData.area_covered_relation ? 
            templateData.area_covered_relation.map((relation: any) => relation.area_covered_id) : [];
          
          formData = {
            title: templateData.title || '',
            content: templateData.content || '',
            content_after_icons: templateData.content_after_icons || '',
            area_covered_ids: areaCoveredIds
          };

          if (templateData.project_type?.display_name) {
            setProjectTypeName(templateData.project_type.display_name);
          }
        } else {
          formData = {
            title: '',
            content: '',
            content_after_icons: '',
            area_covered_ids: []
          };
        }

        setState(prev => ({ 
          ...prev, 
          formData,
          loading: false 
        }));

        form.setFieldsValue(formData);
        
        // Set editor content
        setContentValue(formData.content || '');
        setContentAfterIconsValue(formData.content_after_icons || '');

      } catch (error: any) {
        message.error('Failed to load template data');
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    fetchData();
  }, [projectTypeId, form]);


  const handleSave = async (values: ESRProjectForm) => {
    if (!projectTypeId) return;

    try {
      setState(prev => ({ ...prev, saving: true }));

      await updateProjectTemplate(projectTypeId, values);
      
      message.success('Fallback text saved successfully!');
      setState(prev => ({ 
        ...prev, 
        saving: false,
        formData: values 
      }));
      navigate('/project_types');

    } catch (error: any) {
      message.error('Failed to save fallback text');
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleCancel = () => {
    navigate('/project_types');
  };


  const renderAreaCoveredSelect = () => {
    if (Object.keys(areaCoveredCategories).length > 0) {
      return (
        <Select
          mode="multiple"
          placeholder="Select area coverage"
          showSearch
          optionFilterProp="children"
          style={{ width: '100%' }}
        >
          {Object.entries(areaCoveredCategories).map(([category, options]) => (
            <OptGroup key={category} label={category}>
              {options.map((option) => (
                <Option key={option.id} value={option.id}>
                  {option.display_name}
                </Option>
              ))}
            </OptGroup>
          ))}
        </Select>
      );
    }

    return (
      <Select
        mode="multiple"
        placeholder="Select area coverage"
        showSearch
        optionFilterProp="children"
        style={{ width: '100%' }}
      >
        {state.areaCoveredOptions.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.display_name}
          </Option>
        ))}
      </Select>
    );
  };

  if (state.loading) {
    return (
      <div className="esr-editor-loading">
        <Spin 
          size="large" 
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
        />
      </div>
    );
  }

  return (
    <div className="esr-template-editor">
      <div className="esr-editor-header">
        <Breadcrumb>
          <Breadcrumb.Item>
            <Button 
              type="link" 
              icon={<ArrowLeftOutlined />} 
              onClick={handleCancel}
              style={{ padding: 0 }}
            >
              Project Types
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Edit ESR Project Type Fallback Text</Breadcrumb.Item>
        </Breadcrumb>
        
        <Title level={2} style={{ marginTop: '16px', marginBottom: '8px' }}>
          ESR Project Type Fallback Text Editor
        </Title>
        {projectTypeName && (
          <Typography.Text type="secondary" style={{ fontSize: '16px', marginBottom: '16px', display: 'block' }}>
            Editing fallback text for: <strong>{projectTypeName}</strong>
          </Typography.Text>
        )}
      </div>

      <Card className="esr-editor-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
            initialValues={state.formData}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input 
              placeholder="Enter template title"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: 'Please enter content' }]}
          >
            <SimpleEditor
              initialData={contentValue || ''}
              onChange={(value) => {
                setContentValue(value);
                form.setFieldValue('content', value);
              }}
              editable={true}
            />
          </Form.Item>

          <Form.Item
            label="Area Coverage"
            name="area_covered_ids"
          >
            {renderAreaCoveredSelect()}
          </Form.Item>

          <Form.Item
            label="Content After Icons"
            name="content_after_icons"
          >
            <SimpleEditor
              initialData={contentAfterIconsValue || ''}
              onChange={(value) => {
                setContentAfterIconsValue(value);
                form.setFieldValue('content_after_icons', value);
              }}
              editable={true}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
              <Space size="middle">
                <Button 
                  size="large" 
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  size="large"
                  loading={state.saving}
                  icon={<SaveOutlined />}
                >
                  {state.saving ? 'Saving...' : 'Save & Close'}
                </Button>
              </Space>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ESRTemplateEditor;
